<template>
    <v-container class="customer-management">

        <h2>Mass Recipient Management</h2>

        <!-- Hidden input for file upload - shhhhh -->
        <input
            id="hidden-input-file-upload"
            ref="hidden-input-file-upload"
            type="file"
            accept=".csv"
            @change="readSelectedFile"
        >

        <div class="customer-management__buttons">
            <ClButton
                class="button-export"
                aria-label="Export Recipients"
                :disabled="exportInFlight"
                :loading="exportInFlight"
                @click="exportHandler()"
            >
                Export
            </ClButton>

            <ClButton
                class="button-import"
                aria-label="Import Recipients"
                :disabled="( validationInfo.inFlight || importInFlight )"
                :loading="( validationInfo.inFlight || importInFlight )"
                @click="importHandler()"
            >
                Import
            </ClButton>
        </div>

        <p class="customer-management__disclaimer">
            *Import will only edit existing Recipients
        </p>

        <!-- Import Error Reporting -->
        <div
            v-if="hasValidationErrors"
            class="customer-management__import-errors"
        >
            <p class="customer-management__import-errors__header">
                Import Error: {{ displayedValidationErrorMessage }}
            </p>
            <p
                v-if="hasValidationErrorMessage"
                class="customer-management__import-errors__message"
            >
                {{ validationInfo.message }}
            </p>
            <!-- Error List -->
            <div
                v-if="hasValidationErrorList"
                class="customer-management__import-errors__list"
            >
                <p
                    v-for="( error, index ) in shownValidationErrors"
                    :key="index"
                    class="error-line"
                >
                    {{ formattedLineErrorText( error ) }}
                </p>
            </div>

            <!-- Clear Errors -->
            <ClButton
                type="secondary"
                @click="clearValidationErrors()"
            >
                Clear
            </ClButton>
        </div>
    </v-container>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Api
import CustomerManagementApi from '@/api/CustomerManagementApi';

// Services
import CsvService from '@/services/CsvService';

// Utils
import DateUtil from '@/utils/DateUtil';
import ErrorUtil from '@/utils/ErrorUtil';
import ValidationUtil from '@/utils/ValidationUtil';

export default {
    name: 'CustomerManagement',
    data() {
        return {
            exportInFlight: false,
            importInFlight: false,
            validationInfo: {
                inFlight: false,
                message: '',
                // An array of error objects: [ { attribute: '', value: '', error: '', lineNumber: 1 } ]
                errors: [],
                errorsShownCutoff: 10,
            },
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
            websiteName: ( state ) => state.Website.info.name,
        } ),

        /** @returns { Boolean } */
        hasValidationErrorMessage() {
            return !!this.validationInfo.message;
        },
        /** @returns { Number } */
        validationErrorListCount() {
            return this.validationInfo.errors.length;
        },
        /** @returns { Boolean } */
        hasValidationErrorList() {
            return ( this.validationErrorListCount > 0 );
        },
        /** @returns { Boolean } */
        hasValidationErrors() {
            return this.hasValidationErrorList || this.hasValidationErrorMessage;
        },
        /** @returns { Array } */
        shownValidationErrors() {
            return [ ...this.validationInfo.errors ].slice( 0, this.validationInfo.errorsShownCutoff );
        },
        /** @returns { Number } */
        remainingValidationErrorCount() {
            return ( this.validationErrorListCount - this.validationInfo.errorsShownCutoff );
        },
        /** @returns { String } */
        displayedValidationErrorMessage() {
            let errorMessage = `${ this.validationErrorListCount } errors`;
            if ( this.validationInfo.errors.length > this.validationInfo.errorsShownCutoff ) {
                errorMessage += `- first ${ this.validationInfo.errorsShownCutoff } shown`;
            }
            return errorMessage;
        },
    },
    methods: {
        setValidationErrorMessage( message ) {
            this.validationInfo.message = message;
        },
        clearValidationErrors() {
            this.setValidationErrorMessage( '' );
            this.validationInfo.errors = [];
        },
        /** @returns { String } */
        formattedLineErrorText( {
            attribute,
            value,
            error,
            lineNumber,
        } ) {
            return `${ attribute } error on line ${ lineNumber }. ${ error }. Value is: ${ value }`;
        },

        async exportHandler() {
            this.exportInFlight = true;
            try {
                const { data } = await CustomerManagementApi.getCustomerFileExport( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                } );
                /** @type { String } - example: "RecipientExport-Anderson-University-2021-10-26.csv" */
                const fileName = `RecipientExport-${ this.websiteName.replace( /\s/g, '-' ) }-${ DateUtil.formatDate( Date.now() ) }`;
                CsvService.exportDataAsCsv( data, fileName );
                await this.$store.dispatch( 'snackbar', {
                    message: 'Recipient file exported. Check your downloads.',
                    type: 'success',
                    duration: 5000,
                } );
            } catch ( error ) {
                await this.$store.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                } );
            } finally {
                this.exportInFlight = false;
            }
        },

        async importValidatedCustomerFile( file ) {
            this.importInFlight = true;
            try {
                await CustomerManagementApi.postCustomerCsvImport( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    file,
                } );
                await this.$store.dispatch( 'snackbar', {
                    message: 'File Successfully Imported!',
                    type: 'success',
                    duration: -1,
                } );
            } catch ( error ) {
                await this.$store.dispatch( 'snackbar', {
                    message: 'File Import failed',
                    type: 'error',
                    duration: -1,
                } );
            } finally {
                this.importInFlight = false;
            }
        },
        async remotelyValidateSelectedFile( file ) {
            this.clearValidationErrors();
            this.validationInfo.inFlight = true;
            try {
                await CustomerManagementApi.validateCustomerCsv( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    file,
                } );
                // No need to await on this, it has it's own inFlight flag
                this.importValidatedCustomerFile( file );
            } catch ( error ) {
                // console.error( 'Error: Failed to validate csv file', error );
                this.validationInfo.message = error.response.data.message.toLowerCase() === 'errors in request data'
                    ? 'There are errors on the following lines:'
                    : error.response.data.message;
                this.validationInfo.errors = ValidationUtil.arrayHasItems( error.response.data.lineNumberErrorList )
                    ? error.response.data.lineNumberErrorList
                    : [];
            } finally {
                this.validationInfo.inFlight = false;
            }
        },
        // Battle-tested in Hickory
        // Originally lifted from: https://stackoverflow.com/a/41563966
        csvToArray( text ) {
            let p = '';
            let row = [ '' ];
            const ret = [ row ];
            let i = 0;
            let r = 0;
            let s = !0;
            let l;
            // eslint-disable-next-line guard-for-in,no-restricted-syntax
            for ( l in text ) {
                l = text[ l ];
                if ( l === '"' ) {
                    if ( s && l === p ) row[ i ] += l;
                    s = !s;
                    // eslint-disable-next-line no-multi-assign,no-plusplus
                } else if ( l === ',' && s ) l = row[ ++i ] = '';
                else if ( l === '\n' && s ) {
                    if ( p === '\r' ) row[ i ] = row[ i ].slice( 0, -1 );
                    // eslint-disable-next-line no-multi-assign,no-plusplus
                    row = ret[ ++r ] = [ l = '' ]; i = 0;
                } else row[ i ] += l;
                p = l;
            }
            return ret;
        },
        csvFileHasRequiredHeaders( { fileString } ) {
            const expectedHeaders = [
                'Location External Ref',
                'Location Name',
                'Customer ID',
                'First Name',
                'Last Name',
                'Delivery Type',
                'Drop Off Ref',
                'Pick Up Box Ref',
                'Forward',
            ];
            // Check to see if we at least have all of the expected headers
            // Client-side validation here - flimsy at best
            if ( expectedHeaders.every( ( header ) => fileString.includes( header ) ) ) {
                return true;
            }
            const missingHeaderString = expectedHeaders
                .filter( ( header ) => !fileString.includes( header ) )
                .join( ', ' );
            this.setValidationErrorMessage( `File doesn't have all of the necessary headers. Headers missing: ${ missingHeaderString }` );
            return false;
        },
        processImportedCsv( { file, fileString } ) {
            if ( this.csvFileHasRequiredHeaders( { fileString } ) ) {
                try {
                    const csvArray = this.csvToArray( fileString );
                    // Include lines where only some fields aren't blank
                    const trimmedCsvArray = csvArray.filter( ( lineArray ) => (
                        lineArray.some( ( field ) => field !== '' )
                    ) );
                    // Add a newline character to the last item in each lineArray
                    const csvWithNewLinesArray = trimmedCsvArray.map( ( lineArray ) => {
                        const lastIndex = lineArray.length - 1;
                        const newArray = [ ...lineArray ];
                        newArray[ lastIndex ] = `${ lineArray[ lastIndex ] }\n`;
                        return newArray;
                    } );
                    // Create a new file object
                    const trimmedCsvFile = new File(
                        csvWithNewLinesArray,
                        'CustomerImport.csv',
                        { type: 'text/csv', lastModified: file.lastModified },
                    );
                    this.remotelyValidateSelectedFile( trimmedCsvFile );
                } catch ( error ) {
                    this.setValidationErrorMessage( 'Failed to parse CSV file.' );
                }
            }
        },
        readSelectedFile( event ) {
            const fileList = event.target.files;
            if ( fileList.length !== 1 ) {
                console.log( 'Only one file - no more, no less' );
            } else {
                const fileReader = new FileReader();
                fileReader.onload = ( e ) => {
                    this.processImportedCsv( {
                        file: fileList[ 0 ],
                        fileString: e.target.result,
                    } );
                };
                fileReader.readAsText( fileList[ 0 ] );
            }
        },
        importHandler() {
            this.clearValidationErrors();
            this.$refs[ 'hidden-input-file-upload' ].click();
        },
    },
};
</script>

<style scoped lang="scss">
.customer-management {
    #hidden-input-file-upload {
        display: none;
    }

    &__buttons {
        display: flex;
        flex-flow: row nowrap;
        margin-top: 1rem;
        justify-content: space-between;
        >* {
            width: 48%;
        }
    }

    &__disclaimer {
        margin-top: 1rem;
        margin-bottom: 0;
    }

    &__import-errors {
        &__header {
            border-top: 1px solid gainsboro;
            color: red;
            font-weight: bold;
        }
        &__list {
            display: flex;
            flex-flow: column nowrap;
            .error-line {
                padding-bottom: 0.5rem;
                border-bottom: 1px solid gainsboro;
                margin-bottom: 0.5rem;
            }
        }
    }
}
@include mixins-above(tablet) {
    .customer-management {
        &__buttons {
            justify-content: unset;
            >* {
                width: auto;
                margin: 0;
            }
            .button-import {
                margin-left: 1rem;
            }
        }
    }
}
</style>
